import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../AdminLayout';
import AdminPortfolioCategories from './AdminPortfolioCategories';
import { fetchAdminPortfolioItems, deletePortfolioItem } from '../adminService';
import { useAdminSession } from '../adminSession';
import { ApiError, apiUrl } from '../../../services/apiClient';
import { PortfolioItem } from '../../Portfolio/portfolio.types';
import { formatPortfolioPrice } from '../../Portfolio/portfolioService';
import styles from './AdminPortfolio.module.scss';

function AdminPortfolio() {
  const [session, setSession] = useAdminSession();
  const navigate = useNavigate();
  const [items, setItems] = useState<PortfolioItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const load = () => {
    if (!session) return;
    fetchAdminPortfolioItems(session.token)
      .then(setItems)
      .catch((err) => {
        if (err instanceof ApiError && err.status === 401) {
          setSession(null);
        } else {
          setError(err instanceof Error ? err.message : '주문 상품 목록을 불러오지 못했습니다.');
        }
      });
  };

  useEffect(load, [session, setSession]);

  const itemCounts = useMemo(() => {
    const counts = new Map<string, number>();
    (items ?? []).forEach((item) => counts.set(item.categoryId, (counts.get(item.categoryId) ?? 0) + 1));
    return counts;
  }, [items]);

  const handleDelete = async (item: PortfolioItem) => {
    if (!session) return;
    if (!window.confirm(`"${item.name}" 항목을 삭제하시겠습니까? 되돌릴 수 없습니다.`)) return;

    setActionError(null);
    try {
      await deletePortfolioItem(session.token, item.id);
      load();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : '삭제 중 오류가 발생했습니다.');
    }
  };

  return (
    <AdminLayout title="주문상품 관리">
      <div className="section">
        <div className="sec-head">
          <div className="sec-title">주문 상품 항목</div>
          <div className="sec-actions">
            <span className="chip active">전체 {items?.length ?? 0}건</span>
            <button type="button" className="btn btn-primary" onClick={() => navigate('/admin/portfolio/new')}>
              + 새 주문 상품 등록
            </button>
          </div>
        </div>

        {error && (
          <div className="card" style={{ color: '#B91C1C', marginBottom: 16 }}>
            {error}
          </div>
        )}
        {actionError && (
          <div className="card" style={{ color: '#B91C1C', marginBottom: 16 }}>
            {actionError}
          </div>
        )}

        {!error && (
          <div className="card" style={{ padding: 0 }}>
            <div style={{ overflowX: 'auto' }}>
              <table>
                <thead>
                  <tr>
                    <th>썸네일</th>
                    <th>코드</th>
                    <th>브랜드</th>
                    <th>상품명</th>
                    <th>카테고리</th>
                    <th>기준가</th>
                    <th>공개</th>
                    <th>인기도</th>
                    <th>조회수</th>
                    <th>관리</th>
                  </tr>
                </thead>
                <tbody>
                  {!items && (
                    <tr>
                      <td colSpan={10} className="emptyRow">
                        불러오는 중...
                      </td>
                    </tr>
                  )}
                  {items && items.length === 0 && (
                    <tr>
                      <td colSpan={10} className="emptyRow">
                        등록된 주문 상품이 없습니다.
                      </td>
                    </tr>
                  )}
                  {items?.map((item) => (
                    <tr key={item.id} onClick={() => navigate(`/admin/portfolio/${item.id}/edit`)}>
                      <td>
                        {item.thumbnailUrl ? (
                          <img src={apiUrl(item.thumbnailUrl)} alt={item.name} className={styles.thumb} />
                        ) : (
                          <div className={`${styles.thumbDeco} ${styles[`tone${item.tone}`] ?? ''}`}>{item.deco}</div>
                        )}
                      </td>
                      <td>
                        <span className="idcell">{item.code}</span>
                      </td>
                      <td>{item.brand}</td>
                      <td>{item.name}</td>
                      <td>{item.categoryLabel}</td>
                      <td>{formatPortfolioPrice(item)}</td>
                      <td>
                        <span className={`badgePill ${item.isPublished ? 'ok' : 'neutral'}`}>
                          <span className="pulse"></span> {item.isPublished ? '공개' : '비공개'}
                        </span>
                      </td>
                      <td>{item.popularity}</td>
                      <td>{item.viewCount.toLocaleString()}</td>
                      <td onClick={(event) => event.stopPropagation()}>
                        <div className={styles.rowActions}>
                          <button
                            type="button"
                            className="btn btn-outline"
                            onClick={() => navigate(`/admin/portfolio/${item.id}/edit`)}
                          >
                            수정
                          </button>
                          <button
                            type="button"
                            className={`btn btn-outline ${styles.dangerBtn}`}
                            onClick={() => handleDelete(item)}
                          >
                            삭제
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {session && <AdminPortfolioCategories token={session.token} itemCounts={itemCounts} onChanged={load} />}
    </AdminLayout>
  );
}

export default AdminPortfolio;
